import React, { useState, useEffect } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from 'recharts';
import { useWidget } from 'components/Widget/useWidget';
import CustomTooltip from 'components/charts/components/ChartTooltip';
import './style.css';
import getShortFormattedNumber from 'store/redux/utils/getShortFormattedNumber';
import Ninja from '@ninja';

const ChartBody = ({ height, data, dataKey = 'date', formatter, label, tooltipFormatter }) => {
  const { filters, chartConfig } = useWidget();
  const [visibleMetrics, setVisibleMetrics] = useState(chartConfig?.metrics);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    setVisibleMetrics(chartConfig.metrics);
  }, [chartConfig]);

  const getBarStyle = (index) => {
    if (activeIndex === null) {
      return { opacity: 1, transition: 'all 0.3s ease-in-out' };
    }
    return {
      opacity: index === activeIndex ? 1 : 0.2,
      transition: 'all 0.3s ease-in-out',
    };
  };

  const renderBarsAndLines = () => {
    const bars = [];
    const lines = [];

    Object?.keys(chartConfig?.metrics)?.forEach((key) => {
      const { color, name, type, dashed } = chartConfig.metrics[key];

      const commonProps = {
        key,
        dataKey: key,
        name,
        hide: !visibleMetrics[key]?.show,
        isAnimationActive: true,
        animationDuration: 800,
      };

      if (type === 'line') {
        lines.push(
          <Line
            {...commonProps}
            type='monotone'
            stroke={color}
            fill={color}
            yAxisId='right'
            strokeWidth={2}
            strokeDasharray={dashed && '10 10'}
            style={{
              opacity: activeIndex === null || activeIndex === key ? 1 : 0.4,
              transition: 'all 0.3s ease-in-out',
            }}
            dot={{ r: 2 }}
            activeDot={{ r: 6 }}
          />
        );
      } else {
        bars.push(
          <Bar
            maxBarSize={40}
            {...commonProps}
            stackId={chartConfig.type === 'stacked' ? 'a' : undefined}
            fill={color}
            shape={({ x, y, width, height, index }) => {
              const radius = 3;
              const gap = 2;
              const isNegative = height < 0;
              return (
                <rect
                  x={x}
                  y={isNegative ? y + height + gap / 2 : y + gap / 2}
                  width={width}
                  height={Math.max(0, Math.abs(height) - gap)}
                  rx={radius}
                  ry={radius}
                  fill={color}
                  style={getBarStyle(index)}
                />
              );
            }}
          />
        );
      }
    });

    return [...bars, ...lines];
  };

  return (
    <ResponsiveContainer height={height} width='100%'>
      <ComposedChart
        data={data}
        key={chartConfig.type === 'stacked' ? 'stacked' : 'grouped'}
        stackOffset='sign'
        margin={{
          right: chartConfig.showYAxis ? -20 : 0,
          left: chartConfig.showXAxis ? -20 : 0,
        }}
        onMouseMove={(e) => {
          if (e && e.activeTooltipIndex !== undefined) {
            setActiveIndex(e.activeTooltipIndex);
          }
        }}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <CartesianGrid strokeDasharray='2 2' vertical={false} stroke={Ninja.colors.xlight} />
        <XAxis
          axisLine={false}
          dataKey={dataKey}
          dy={5}
          tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
          tickFormatter={(value) =>
            formatter
              ? formatter(value)
              : new Date(value).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: '2-digit',
                })
          }
          hide={!chartConfig.showXAxis}
        />

        <YAxis
          axisLine={false}
          hide={!chartConfig.showYAxis}
          tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
          tickFormatter={(value) => getShortFormattedNumber(value)}
        />
        <YAxis
          axisLine={false}
          tick={{ fontSize: 12, fontWeight: '400', fill: Ninja.colors.dark }}
          hide={!chartConfig.showYAxis}
          yAxisId='right'
          orientation='right'
          tickFormatter={(value) => getShortFormattedNumber(value)}
        />
        <Tooltip
          cursor={{
            stroke: Ninja.colors.info,
            strokeWidth: 1,
            strokeDasharray: '2 2',
            fill: Ninja.colors.info,
            transition: 'all 0.2s ease-in-out',
          }}
          content={
            tooltipFormatter || (
              <CustomTooltip
                dataKey={dataKey}
                filters={filters}
                metricsList={visibleMetrics}
                label={label}
              />
            )
          }
        />
        <ReferenceLine y={0} stroke={Ninja.colors.xlight} yAxisId='right' />
        <ReferenceLine y={0} stroke={Ninja.colors.xlight} />
        {renderBarsAndLines()}
        <Legend
          onClick={(e) => {
            const { dataKey } = e;
            setVisibleMetrics((prevMetrics) => ({
              ...prevMetrics,
              [dataKey]: {
                ...prevMetrics[dataKey],
                show: !prevMetrics[dataKey]?.show,
              },
            }));
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default ChartBody;
